import { CancelTokenSource } from 'axios';
import { formatWithFullPath } from '../helpers/tribe';
import {
  ApplicationTotal,
  ExceptionView,
  IApplication,
  IApplicationFull,
  IApplicationsLanguages,
  IApplicationsPayload,
  ICertified,
  IEntityAggrByDate,
  INode,
  IResponseUser,
  ITotal,
  ITotalRecord,
  MaterializedFilters,
  RecommendationNextView,
  ResultKey,
  ResultPaginated,
  RoadmapView,
} from '../types';
import { ApiRepository } from './main repository/api.repository';

export const getApplications = async (url: string): Promise<IApplicationsPayload> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/${url}`;
  return ApiRepository.Core().get<IApplicationsPayload>(apiUrl);
};

export const getMyApplicationsCount = async (): Promise<number> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-next/counts`;
  return ApiRepository.Core().get<number>(apiUrl, { owned: true });
};

export const getTribes = async (cancelTokenSource: CancelTokenSource, recordDate: string): Promise<INode[]> => {
  const result = await ApiRepository.Core().get<Array<INode>>(
    `${window.globalConfiguration.api.base}/v1/applications/results/global_taxon_tribe`,
    { format: true, recordTo: recordDate, recordFrom: recordDate },
    { cancelToken: cancelTokenSource.token }
  );
  if (result) {
    formatWithFullPath(result);
  }
  return result;
};

export const getApplicationsCount = (aggrByDate: boolean): Promise<number | IEntityAggrByDate[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-nexts/counts`;
  return ApiRepository.Core().get<number | IEntityAggrByDate[]>(apiUrl, { aggrByDate });
};

export const getAvailableDates = (): Promise<string[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-next/available-dates`;
  return ApiRepository.Core().get<string[]>(apiUrl);
};

export const getApplicationResultBykey = (
  resultKey: ResultKey,
  recordFrom: string,
  recordTo?: string
): Promise<string[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/results/${resultKey}`;
  return ApiRepository.Core().get<string[]>(apiUrl, {
    recordTo: recordTo ?? recordFrom,
    recordFrom,
  });
};

export const getFilters = <T = MaterializedFilters>(recordDate: string, filterName?: string): Promise<T> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/filters`;
  return ApiRepository.Core().get<T>(apiUrl, { recordDate, filterName });
};

export const getFormattedFilter = async (
  resultKey: 'global_taxon_managed-by' | 'global_taxon_tribe' | 'global_taxon_functional-architecture',
  recordDate: string,
  query?: string
): Promise<INode[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/results/${resultKey}`;
  return ApiRepository.Core().get<INode[]>(apiUrl, {
    format: true,
    recordTo: recordDate,
    recordFrom: recordDate,
    query,
  });
};

export const addOwners = async (applicationId: number, userEmails: string[]): Promise<IResponseUser> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/owners`;
  return ApiRepository.Core().post<{ emails: string[] }, IResponseUser>(apiUrl, {
    emails: userEmails,
  });
};

export const deleteOwner = async (applicationId: number, userId: number): Promise<IResponseUser> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/owners/${userId}`;
  return ApiRepository.Core().delete<IResponseUser>(apiUrl);
};

export const addTags = async (applicationIds: number[], tags: string[]): Promise<IApplication[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/tags`;
  return ApiRepository.Core().post<{ ids: number[]; tags: string[] }, IApplication[]>(apiUrl, {
    ids: applicationIds,
    tags,
  });
};

export const removeTag = async (applicationId: number, tagId: number): Promise<IApplication> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/tags/${tagId}`;
  return ApiRepository.Core().delete<IApplication>(apiUrl);
};

export const getTotalResultFull = async (
  cancelTokenSource: CancelTokenSource,
  filters: string,
  aggrByDate: boolean
): Promise<ITotal[]> => {
  return ApiRepository.Core().get(
    `${window.globalConfiguration.api.base}/v1/results-next/total-results-full${filters}`,
    { aggrByDate },
    { cancelToken: cancelTokenSource.token }
  );
};

export const getTotal = async (
  cancelTokenSource: CancelTokenSource,
  filters: Record<string, string | null | undefined | boolean | number | Array<string | null>>,
  recordFrom: string | null | undefined,
  recordTo: string | null | undefined
): Promise<ApplicationTotal> => {
  return ApiRepository.Core().get(
    `${window.globalConfiguration.api.base}/v1/results-next/total`,
    { ...filters, recordFrom, recordTo },
    { cancelToken: cancelTokenSource.token }
  );
};

export const sendMailToOwners = async (
  applicationIds: number[],
  subject: string,
  content: string
): Promise<ITotal[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/owners/do-notify`;
  return ApiRepository.Core().post<{ applicationIds: number[]; subject: string; content: string }, ITotal[]>(apiUrl, {
    applicationIds,
    subject,
    content,
  });
};

export const getApplicationFilterByResult = async (
  query: string,
  recordDate: string,
  page: number,
  limit: number,
  componentRefId?: number,
  scopeRefId?: number,
  searchMode?: string,
  version?: string
): Promise<ResultPaginated> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/results`;
  return ApiRepository.Core().get<ResultPaginated>(apiUrl, {
    query,
    recordDate,
    componentRefId,
    scopeRefId,
    searchMode,
    version,
    page,
    limit,
  });
};

export const getApplicationComponentStatus = async (
  applicationIds: string[],
  recordTo: string
): Promise<ICertified[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/certified`;
  return ApiRepository.Core().get<ICertified[]>(apiUrl, {
    recordTo,
    applicationIds: applicationIds.join(','),
  });
};

export const getCertifiedApplicationsCount = async (): Promise<number | IEntityAggrByDate[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/count-certifications`;
  return ApiRepository.Core().get<number | IEntityAggrByDate[]>(apiUrl);
};

export const getTotalResultByRecord = async (
  cancelTokenSource: CancelTokenSource,
  filters: string
): Promise<ITotalRecord[]> => {
  return ApiRepository.Core().get<ITotalRecord[]>(
    `${window.globalConfiguration.api.base}/v1/results-next/total-cost-records`,
    { filters },
    { cancelToken: cancelTokenSource.token }
  );
};

export const chaseOwners = async (
  applicationIds: number[],
  content: string,
  sendToMyself?: boolean
): Promise<ITotal[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/owners/do-chasing`;
  return ApiRepository.Core().post<
    { applicationIds: number[]; mailBody: string; sendToMyself: boolean; action: string },
    ITotal[]
  >(apiUrl, {
    sendToMyself: sendToMyself ?? false,
    applicationIds,
    mailBody: content,
    action: 'send-mail',
  });
};

export const getIAppliName = async (applicationIds: number[]): Promise<IApplicationFull[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/iAppliNames`;
  return ApiRepository.Core().get<IApplicationFull[]>(apiUrl, { applicationIds: applicationIds.join(',') });
};

export const getApplicationIds = async (
  iAppliIDs: string[] | undefined,
  kearIDs: string[] | undefined
): Promise<number[]> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/applicationIds`;
  return ApiRepository.Core().get<number[]>(apiUrl, { iAppliIDs, kearIDs });
};

export const getLanguagesByApp = async (
  applicationId: number,
  recordDate?: string
): Promise<IApplicationsLanguages[] | null> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-next/${applicationId}/languages`;
  return ApiRepository.Core().get<IApplicationsLanguages[] | null>(apiUrl, { recordDate });
};

export const getLastUpdateDate = async (): Promise<Date> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-next/last-update-date`;
  return ApiRepository.Core().get<Date>(apiUrl);
};

export const getApplicationExceptions = async (applicationId: number, withRelations = false, withDeleted = false) => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/exceptions`;

  return ApiRepository.Core().get<Array<ExceptionView>>(apiUrl, {
    with_relations: withRelations,
    with_deleted: withDeleted,
  });
};

export const getApplicationRecommendations = async (
  applicationId: number,
  withProcessed = false,
  withRelations = false
) => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/recommendations-next`;

  const recommendations = await ApiRepository.Core().get<Array<RecommendationNextView>>(apiUrl, {
    with_relations: withRelations,
  });

  return withProcessed
    ? recommendations
    : recommendations.filter(
        (recommendation) =>
          !['processed-with-remediation', 'processed-without-remediation'].includes(recommendation.status)
      );
};

export const getApplicationRoadmaps = async ({
  applicationId,
  withRelations = false,
  withDeleted = false,
  componentRefKey,
}: {
  applicationId: number;
  withRelations?: boolean;
  withDeleted?: boolean;
  componentRefKey?: string;
}) => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/roadmaps`;

  return ApiRepository.Core().get<Array<RoadmapView>>(apiUrl, {
    with_relations: withRelations,
    with_deleted: withDeleted,
    componentRefKey,
  });
};

export const getApplicationMaxRecordDate = async (applicationId: number): Promise<string | null> => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/results-next/${applicationId}/max-record-date`;
  return ApiRepository.Core().get<string | null>(apiUrl);
};

export const getTransformationPathEvolution = ({ applicationId }: { applicationId: number }) => {
  const apiUrl = `${window.globalConfiguration.api.base}/v1/applications/${applicationId}/transformation-path/evolution`;

  return ApiRepository.Core().get<Array<{ recordDate: string; name: string; count: number }>>(apiUrl);
};
